import moment from "moment-timezone";
import FormElement from "rc-field-form";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../../../../components/Button";
import { generateErrorsConfigForForm } from "../../../../../utils/helpers/errors";
import { useModuleAccess } from "../../../../../utils/hooks/access";
import { useCompany, useIsMasterCompany } from "../../../../../utils/hooks/company";
import { useIsFieldDisabled } from "../../../helpers/useIsFieldDisabled";
import Content from "./Content";
import { omit } from "lodash";

function Form({ mode, values, error, loading, submit, close }) {
    const { t } = useTranslation();
    const [form] = FormElement.useForm();
    const company = useCompany();
    const isMasterCompany = useIsMasterCompany();
    const { access } = useModuleAccess("settings.payPolicies.holidays");
    const disabled = useIsFieldDisabled({ ...access, mode });

    const onClose = useCallback(() => {
        form.resetFields();
        close();
    }, [form, close]);

    const onFinish = useCallback(
        (formValues) => {
            const { country, state, ...values } = formValues;

            const probation = formValues?.type
                ? {
                      type: formValues.type,
                      value: formValues?.value,
                  }
                : null;

            const data = {
                ...values,
                days: values?.days.reduce((total, day) => {
                    if (day?.dates?.length > 0 && day?.dates?.[0] !== "") {
                        delete day.date;
                        const dates = (day.dates || []).map((date) => {
                            return moment(date).format("YYYY-MM-DD");
                        });
                        total.push({ ...day, dates });
                    } else {
                        delete day.dates;
                        const date = day?.date && moment(day.date).format("YYYY-MM-DD");
                        total.push({ ...day, date });
                    }
                    return total;
                }, []),
                probation,
            };
            submit(data);
        },
        [submit]
    );

    useEffect(() => {
        form.resetFields();
        form.setFieldsValue({
            status: "active",
            ...values,
            probation: values?.probation?.type ? true : false,
            type: values?.probation?.type,
            value: values?.probation?.value,
            days: values?.days
                ? values.days.map((day) => ({
                      ...day,
                      isPremium: !!day?.isPremium,
                      date: day?.date && moment(day.date).toDate(),
                      dates: day?.dates ? day.dates.map((date) => moment(date).toDate()) : [],
                  }))
                : [
                      {
                          name: undefined,
                          repeatEveryYear: true,
                          isSameDate: true,
                          isPremium: false,
                      },
                  ],
        });
    }, [form, values, company]);

    useEffect(() => {
        const fieldErrors = generateErrorsConfigForForm(["code", "description", "status", "days"], error);
        form.setFields(fieldErrors);
    }, [error, form]);

    return (
        <FormElement form={form} className="d-flex flex-column justify-content-between h-100" onFinish={onFinish}>
            <div>
                <Content mode={mode} form={form} disabled={disabled || !isMasterCompany} data={values} />
            </div>

            <div className="d-flex justify-content-end">
                <Button
                    onClick={onClose}
                    disabled={loading}
                    className="btn-round btn-icon shadow-none border btn btn-secondary btn-sm"
                >
                    {t("cancel")}
                </Button>

                {!disabled && isMasterCompany && (
                    <Button type="submit" className="btn-dark btn-sm shadow-none" loading={loading}>
                        {t("save")}
                    </Button>
                )}
            </div>
        </FormElement>
    );
}

export default Form;
